import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../contexts/auth.context";
import { Permission } from "common/build/prisma/client";
import Spinner from "../layout/Spinner";
import { isSoloPersonalOrganisation } from "../../utils/courseDemo";

type BaseRouteProps = {
    permission?: Permission;
    /**
     * Restrict the route to users in the Verbal organisation (`isVerbal`, i.e. organisation
     * name === "Verbal" — NOT `isSchool`, which is orgType). Use for internal Verbal tooling
     * that partner organisations must not reach. Enforced here rather than only in the nav:
     * hiding a link leaves the URL typeable.
     */
    verbalOnly?: boolean;
    layout?: React.ComponentType<{ children: React.ReactNode }>;
    component: React.ComponentType<Record<string, unknown>>;
    /** Individual (personal) orgs cannot use multi-user admin screens. Demo orgs that are not Individual can. */
    denyDemo?: boolean;
    /**
     * Phase 3 Assess is not finished for school accounts. Hide the URL, not just the nav item:
     * School Admin is FullAccess, so a permit check alone would still open /assess.
     */
    denySchool?: boolean;
};

/**
 * Route guard component for admin routes with authentication and permission checks
 * Migrated to React Router v6 pattern
 */
export const BaseRoute: React.FC<BaseRouteProps> = ({
    permission,
    verbalOnly,
    layout: Layout,
    component: Component,
    denyDemo,
    denySchool,
}) => {
    const { isAuthenticated, isAuthenticating, hasPermission, user } = useAuth();

    // Handle authentication check
    if (permission || verbalOnly || denySchool) {
        if (!isAuthenticated) {
            if (isAuthenticating) {
                return <Spinner />;
            }
            return <Navigate to="/login" replace />;
        }

        // Handle permission check
        if (permission && !hasPermission(permission)) {
            return <Navigate to="/403" replace />;
        }

        // Handle Verbal-organisation check
        if (verbalOnly && !user?.isVerbal) {
            return <Navigate to="/403" replace />;
        }

        if (denySchool && user?.isSchool) {
            return <Navigate to="/403" replace />;
        }

        if (denyDemo && isSoloPersonalOrganisation(user?.Organisation, user?.isSchool)) {
            return <Navigate to="/home" replace />;
        }
    }

    // Render the component with optional layout wrapper
    return (
        <>
            {Layout ? (
                <Layout>
                    <Component />
                </Layout>
            ) : (
                <Component />
            )}
        </>
    );
};
